import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Home,
  Workflow,
  Tag,
  FileText,
  Sparkles,
  PhoneCall,
  Accessibility,
  Info,
  Briefcase,
  HelpCircle,
  Mail,
  Sun,
  Moon,
  Smartphone,
} from "lucide-react";
import { AdminLoginModal } from "./AdminLoginModal";
import { useTheme } from "../hooks/useTheme";

export const LANGUAGES = {
  en: { name: "English",  flag: "gb" },
  pl: { name: "Polski",   flag: "pl" },
  de: { name: "Deutsch",  flag: "de" },
  fr: { name: "Français", flag: "fr" },
  es: { name: "Español",  flag: "es" },
};

// SVG flags via flagcdn.com — reliable across all platforms (Windows
// Chrome, Linux Firefox, etc.) where Unicode flag emoji are not
// rendered as colour glyphs.
const flagSrc = (code) => `https://flagcdn.com/h40/${code}.png`;
const flagSrcSet = (code) => `https://flagcdn.com/h80/${code}.png 2x, https://flagcdn.com/h120/${code}.png 3x`;

// 10-tap easter-egg admin trigger.
const TAP_THRESHOLD = 10;
const TAP_WINDOW_MS = 5000;

/**
 * Single nav set — every link drops into the mega-menu card.
 * Each item carries its own lucide icon so the menu rows mirror
 * the zen.com `Shop Smarter / Send · Pay · Exchange / Travel Better`
 * aesthetic (green icon left · label centre · ↗ right).
 */
const NAV_ITEMS = [
  { key: "navHome",          fallback: "Home",              href: "/",                  Icon: Home },
  { key: "navDownloadApp",   fallback: "Download app",      href: "/download",          Icon: Smartphone },
  { key: "navHowItWorks",    fallback: "How It Works",      href: "/how-it-works",      Icon: Workflow },
  { key: "navPricing",       fallback: "Pricing",           href: "/pricing",           Icon: Tag },
  { key: "navAiCall",        fallback: "AI Call Assistant", href: "/ai-call-assistant", Icon: PhoneCall },
  { key: "navWhyWorthify",   fallback: "Why Worthify",      href: "/why-worthify",      Icon: Sparkles },
  { key: "navAccessibility", fallback: "Accessibility",     href: "/accessibility",     Icon: Accessibility },
  { key: "navAbout",         fallback: "About",             href: "/about",             Icon: Info },
  { key: "navInvestors",     fallback: "For Investors",     href: "/for-investors",     Icon: Briefcase },
  { key: "navFaq",           fallback: "FAQ",               href: "/faq",               Icon: HelpCircle },
  { key: "navContact",       fallback: "Contact",           href: "/contact",           Icon: Mail },
];

export const AppHeader = ({ t, language, setLanguage, onLogoClick }) => {
  const tapRef = useRef({ count: 0, last: 0 });
  const [adminOpen, setAdminOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /* Scroll → bar deepens after 60px. */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Escape closes everything. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setMenuOpen(false); setLangOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* Lock body scroll while a sheet is open. */
  useEffect(() => {
    const original = document.body.style.overflow;
    if (menuOpen || langOpen) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, [menuOpen, langOpen]);

  /* Close on route change. */
  useEffect(() => {
    setMenuOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const inApp =
    typeof window !== "undefined" &&
    (window.__WORTHIFY_APP__ === true ||
      (() => { try { return localStorage.getItem("worthify_in_app") === "1"; } catch { return false; } })() ||
      (() => { try { return new URLSearchParams(window.location.search).get("worthify_app") === "1"; } catch { return false; } })() ||
      (typeof navigator !== "undefined" && (navigator.userAgent || "").includes("WorthifyApp")));

  const items = NAV_ITEMS
    .filter((it) => !(inApp && it.href === "/download"))
    .map((it) => {
      const tr = t ? t(it.key) : null;
      const label = tr && tr !== it.key ? tr : it.fallback;
      return { ...it, label };
    });

  const isActive = (href) =>
    location.pathname === href ||
    (href === "/ai-call-assistant" && location.pathname === "/ai-call") ||
    (href === "/for-investors"     && location.pathname === "/investors");

  const handleLogoTap = () => {
    if (typeof onLogoClick === "function") onLogoClick();
    const now = Date.now();
    const state = tapRef.current;
    if (now - state.last > TAP_WINDOW_MS) state.count = 0;
    state.count += 1;
    state.last = now;
    if (state.count >= TAP_THRESHOLD) { state.count = 0; setAdminOpen(true); }
  };

  const { theme, toggleTheme } = useTheme();

  return (
    <>
      <style>{styles}</style>

      <motion.header
        className="wf-nav"
        data-testid="wf-nav"
        data-scrolled={scrolled ? "true" : "false"}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      >
        <div className="wf-nav__inner">
          {/* Left: flag circle */}
          <button
            type="button"
            className="wf-lang-circle"
            onClick={() => setLangOpen(true)}
            aria-label={`Change language (current: ${(LANGUAGES[language] || LANGUAGES.en).name})`}
            data-testid="language-button"
          >
            <img
              src={flagSrc((LANGUAGES[language] || LANGUAGES.en).flag)}
              srcSet={flagSrcSet((LANGUAGES[language] || LANGUAGES.en).flag)}
              alt=""
              aria-hidden="true"
              className="wf-lang-circle__flag"
              draggable={false}
            />
          </button>

          {/* Centre: logo wordmark */}
          <button
            type="button"
            className="wf-brand"
            onClick={handleLogoTap}
            aria-label="Worthify — go to home"
            data-testid="worthify-logo"
          >
            <img
              src={theme === "light" ? "/worthify-logo-dark.png" : "/worthify-logo.png"}
              alt="Worthify — Is it really worth it?"
              className="wf-brand__mark"
              style={{ mixBlendMode: theme === "light" ? "darken" : "normal" }}
              draggable={false}
            />
          </button>

          {/* Right: theme toggle + hamburger */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              className="wf-theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              aria-pressed={theme === "light"}
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun size={18} strokeWidth={2.2} /> : <Moon size={18} strokeWidth={2.2} />}
            </button>
            <button
              type="button"
              className={`wf-burger ${menuOpen ? "wf-burger--open" : ""}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="wf-mega"
              data-testid="mobile-nav-trigger"
            >
              <span className="wf-burger__bar" />
              <span className="wf-burger__bar" />
              <span className="wf-burger__bar" />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Premium Framer Motion Mega Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mega-menu-overlay"
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(16px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[999] flex items-start justify-center pt-24 sm:pt-28 px-4"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-nav-overlay"
          >
            <motion.div
              id="wf-mega"
              initial={{ opacity: 0, scale: 0.85, y: -20, rotateX: -15 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10, rotateX: -5 }}
              transition={{ type: "spring", damping: 18, stiffness: 200, mass: 0.8 }}
              className="relative overflow-hidden w-[96vw] max-w-[1400px]"
              style={{
                background: 'radial-gradient(120% 120% at 50% 0%, rgba(15, 23, 42, 0.9) 0%, rgba(6, 8, 16, 0.95) 100%)',
                border: '1px solid rgba(52, 211, 153, 0.15)',
                borderRadius: '32px',
                boxShadow: '0 50px 100px -20px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255,255,255,0.1), 0 0 40px rgba(52, 211, 153, 0.05)',
                padding: '32px',
                transformPerspective: 1000
              }}
              role="dialog"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative top glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[40px] bg-emerald-500/20 blur-[40px] rounded-full pointer-events-none" />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 relative z-10">
                {items.map((item, idx) => {
                  const Icon = item.Icon;
                  const active = isActive(item.href);
                  return (
                    <motion.div
                      key={item.key}
                      initial={{ opacity: 0, y: 15, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 0.1 + (idx * 0.04), type: "spring", stiffness: 250, damping: 20 }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setMenuOpen(false)}
                        className={`group flex items-center gap-4 p-4 rounded-[20px] transition-all duration-300 ${
                          active 
                            ? 'bg-emerald-500/10 border border-emerald-500/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' 
                            : 'bg-white/5 border border-white/5 hover:bg-white/10 hover:border-emerald-500/20 hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)]'
                        }`}
                      >
                        <div className={`flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 ${
                          active
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-black/20 text-slate-300 group-hover:bg-emerald-500/15 group-hover:text-emerald-400'
                        }`}>
                          <Icon size={20} strokeWidth={2.5} />
                        </div>
                        <span className={`font-semibold text-[16px] flex-1 transition-colors duration-300 ${
                          active ? 'text-emerald-300' : 'text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:text-white'
                        }`}>
                          {item.label}
                        </span>
                        <ArrowUpRight 
                          size={18} 
                          className={`transition-all duration-300 ${
                            active ? 'opacity-100 text-emerald-400 transform translate-x-1 -translate-y-1' : 'opacity-30 text-slate-400 group-hover:opacity-100 group-hover:text-emerald-400 group-hover:translate-x-1 group-hover:-translate-y-1'
                          }`} 
                        />
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Premium Framer Motion Language Picker */}
      <AnimatePresence>
        {langOpen && (
          <motion.div
            key="lang-sheet-overlay"
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(16px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[1100] flex items-center justify-center p-4"
            onClick={() => setLangOpen(false)}
            data-testid="lang-sheet"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 20, rotateX: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10, rotateX: 5 }}
              transition={{ type: "spring", damping: 18, stiffness: 200, mass: 0.8 }}
              className="w-full max-w-sm relative overflow-hidden"
              style={{
                background: 'radial-gradient(120% 120% at 50% 0%, rgba(15, 23, 42, 0.95) 0%, rgba(6, 8, 16, 0.98) 100%)',
                border: '1px solid rgba(52, 211, 153, 0.2)',
                borderRadius: '32px',
                boxShadow: '0 50px 100px -20px rgba(0, 0, 0, 0.8), inset 0 1px 0 rgba(255,255,255,0.1), 0 0 40px rgba(52, 211, 153, 0.08)',
                padding: '28px',
                transformPerspective: 1000
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Choose language"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative top glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-[1px] bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-[40px] bg-emerald-500/25 blur-[30px] rounded-full pointer-events-none" />

              <div className="relative z-10 flex flex-col gap-5">
                <h2 className="text-center text-slate-900 dark:text-white text-[19px] font-bold tracking-tight mb-2">
                  {t('chooseLanguage') || 'Choose your language'}
                </h2>
                
                <div className="grid gap-3">
                  {Object.entries(LANGUAGES).map(([code, { name, flag }], idx) => {
                    const active = code === language;
                    return (
                      <motion.div
                        key={code}
                        initial={{ opacity: 0, x: -15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + (idx * 0.05), type: "spring", stiffness: 200, damping: 20 }}
                      >
                        <button
                          type="button"
                          className={`w-full group flex items-center gap-4 p-3.5 rounded-[18px] transition-all duration-300 ${
                            active 
                              ? 'bg-emerald-500/15 border border-emerald-500/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' 
                              : 'bg-white/5 border border-white/5 hover:bg-white/10 hover:border-emerald-500/30 hover:shadow-[0_8px_20px_rgba(0,0,0,0.2)]'
                          }`}
                          onClick={() => { setLanguage(code); setLangOpen(false); }}
                          data-testid={`lang-option-${code}`}
                        >
                          <img
                            src={flagSrc(flag)}
                            srcSet={flagSrcSet(flag)}
                            alt=""
                            aria-hidden="true"
                            className="w-8 h-6 object-cover rounded-[6px] shadow-sm flex-shrink-0"
                            draggable={false}
                          />
                          <span className={`font-bold text-[14px] uppercase tracking-widest w-10 opacity-70 transition-colors duration-300 ${
                            active ? 'text-emerald-300' : 'text-slate-400 group-hover:text-emerald-200'
                          }`}>
                            {code}
                          </span>
                          <span className={`font-semibold text-[16px] flex-1 text-left transition-colors duration-300 ${
                            active ? 'text-emerald-300' : 'text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:text-white'
                          }`}>
                            {name}
                          </span>
                        </button>
                      </motion.div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="mt-2 self-center px-6 py-2.5 rounded-full border border-white/10 bg-transparent text-slate-900 dark:text-white text-sm font-semibold hover:bg-white/5 transition-all duration-200"
                  onClick={() => setLangOpen(false)}
                  aria-label="Close language picker"
                >
                  {t('close') || 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AdminLoginModal open={adminOpen} onOpenChange={setAdminOpen} />
    </>
  );
};

const styles = `
.wf-nav {
  position: fixed;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 1000;
  height: 56px;
  background: var(--wf-nav-bg);
  backdrop-filter: blur(32px) saturate(200%);
  -webkit-backdrop-filter: blur(32px) saturate(200%);
  border: 1px solid var(--wf-nav-border);
  border-top: none;
  border-radius: 0 0 35px 35px;
  box-shadow: 0 10px 40px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08);
  transition: background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
@media (min-width: 1060px) {
  .wf-nav { height: 77px; top: 0px; left: 96px; right: 96px; }
}
.wf-nav[data-scrolled="true"] {
  background: var(--wf-nav-bg-scrolled);
  box-shadow: 0 20px 50px -10px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05);
  border-color: var(--wf-nav-border-scrolled);
}
@media (min-width: 1060px) {
  .wf-nav[data-scrolled="true"] { 
    top: 0px; 
    height: 77px; 
  }
}
.wf-nav__inner {
  height: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}
@media (min-width: 1060px) { .wf-nav__inner { padding: 0 22px; } }

/* Flag circle (left) */
/* Lang circle — flag button (left) */
.wf-lang-circle {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
  background: var(--wf-nav-lang-bg);
  border: 1px solid var(--wf-nav-lang-border);
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition: border-color 150ms ease, background 150ms ease, transform 200ms ease;
}
.wf-lang-circle:hover { border-color: #22c55e; background: rgba(34,197,94,0.08); transform: scale(1.04); }
.wf-lang-circle__flag {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  display: block;
  box-shadow: 0 1px 3px rgba(0,0,0,0.35);
}
@media (min-width: 1024px) {
  .wf-lang-circle { width: 40px; height: 40px; min-width: 40px; }
  .wf-lang-circle__flag { width: 100%; height: 100%; }
}

/* Brand — centered */
.wf-brand {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1),
              filter 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.wf-brand:hover { transform: translate(-50%, -50%) scale(1.04); filter: drop-shadow(0 0 12px rgba(34,197,94,0.45)); }
.wf-brand__mark { height: 36px; width: auto; display: block; }
@media (min-width: 1024px) { .wf-brand__mark { height: 64px; } }

/* Hamburger */
.wf-burger {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 999px;
  background: #22c55e;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  padding: 0;
  transition: background-color 200ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.wf-burger:hover { background: #16a34a; }
.wf-burger__bar {
  display: block;
  width: 18px;
  height: 2px;
  background: #fff;
  border-radius: 2px;
  transform-origin: center;
  transition: transform 220ms cubic-bezier(0.4, 0, 0.2, 1),
              opacity 180ms cubic-bezier(0.4, 0, 0.2, 1);
}
.wf-burger--open { background: transparent; border: 1px solid #22c55e; }
.wf-burger--open .wf-burger__bar { background: #22c55e; }
.wf-burger--open .wf-burger__bar:nth-child(1) { transform: translateY(6px) rotate(45deg); }
.wf-burger--open .wf-burger__bar:nth-child(2) { opacity: 0; }
.wf-burger--open .wf-burger__bar:nth-child(3) { transform: translateY(-6px) rotate(-45deg); }
@media (min-width: 1024px) {
  .wf-burger { width: 44px; height: 44px; min-width: 44px; }
  .wf-burger__bar { width: 20px; }
}

/* Theme toggle pill (sun ↔ moon) */
.wf-theme-toggle {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
  background: var(--wf-nav-lang-bg);
  border: 1px solid var(--wf-nav-lang-border);
  color: var(--wf-nav-brand);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease, transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.wf-theme-toggle:hover {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.10);
  color: #22c55e;
  transform: rotate(20deg);
}
@media (min-width: 1024px) {
  .wf-theme-toggle { width: 40px; height: 40px; min-width: 40px; }
}

/* Mega menu wrap — captures outside clicks + scroll lock */
/* Removed old .wf-mega CSS block since it's now handled entirely by Framer Motion and Tailwind */

/* Removed old .wf-langsheet CSS block since it's now handled by Framer Motion and Tailwind */

/* Focus-visible — green ring */
.wf-nav :focus-visible,
.wf-mega :focus-visible,
.wf-langsheet :focus-visible {
  outline: 2px solid #22c55e;
  outline-offset: 2px;
}

/* Reduced motion — skipped inside the native app (.wf-force-motion) for 1:1 parity */
@media (prefers-reduced-motion: reduce) {
  html:not(.wf-force-motion) .wf-nav, html:not(.wf-force-motion) .wf-brand, html:not(.wf-force-motion) .wf-burger, html:not(.wf-force-motion) .wf-burger__bar,
  html:not(.wf-force-motion) .wf-mega-wrap, html:not(.wf-force-motion) .wf-mega, html:not(.wf-force-motion) .wf-mega__row, html:not(.wf-force-motion) .wf-mega__icon, html:not(.wf-force-motion) .wf-mega__arrow,
  html:not(.wf-force-motion) .wf-langsheet, html:not(.wf-force-motion) .wf-langsheet__btn, html:not(.wf-force-motion) .wf-langsheet__close,
  html:not(.wf-force-motion) .wf-lang-circle {
    transition: none !important;
    animation: none !important;
  }
}
`;
